import React, { PropsWithChildren } from 'react'
import { useDebounce } from '@node-space/hooks'
import {
  Box,
  EmptyState,
  EmptyStateProps,
  Pagination,
  PaginationProps,
  RowsSkeletonLoader,
} from '@node-space/ui-components'
import { Lottie } from 'components/Animations/ThemeLottie'
import { RequestErrorStatusCard } from 'components/Errors/RequestErrorStatusCard'
import { PageHeader, type PageHeaderProps } from 'components/header/PageHeader'
import NoSearchResults from 'components/NoSearchResults/NoSearchResults'
import { SearchAndFilter, SearchAndFilterProps } from 'components/SearchAndFilter/SearchAndFilter'
import { PAGE_SIZE } from 'pages/EmbeddedCustomers/helpers/constants'

export interface ResultsLayoutProps extends PropsWithChildren {
  isLoading: boolean
  isError: boolean
  hasResults: boolean
  hasSearchAndFilter: boolean
  emptyState: Omit<EmptyStateProps, 'Illustration' | 'testId' | 'LottiePlayer'>
  header?: PageHeaderProps
  searchAndFilter: Omit<SearchAndFilterProps, 'testId'>
  pagination?: Omit<PaginationProps, 'testId'>
  testId: string
  refetchResults: () => void
  resetSearchAndFilters: () => void
  showSearchAndFilter?: boolean
  introduction?: React.ReactNode
  tableHead?: React.ReactNode
  dashboardEmptyState?: boolean
  enableEpcDashboard?: boolean
}

export const ResultsLayout = ({
  isLoading,
  isError,
  hasResults,
  hasSearchAndFilter,
  emptyState,
  searchAndFilter,
  pagination,
  children,
  testId,
  refetchResults,
  resetSearchAndFilters,
  showSearchAndFilter,
  header,
  introduction,
  tableHead,
  dashboardEmptyState,
  enableEpcDashboard,
}: ResultsLayoutProps) => {
  const hasSearchAndFilterDebounced = useDebounce(hasSearchAndFilter, 300)
  const pageSize = pagination?.rowsPerPage ?? PAGE_SIZE

  if (isError) {
    return <RequestErrorStatusCard onReload={refetchResults} />
  }

  if (
    !isLoading &&
    !hasResults &&
    !hasSearchAndFilterDebounced &&
    !dashboardEmptyState &&
    !enableEpcDashboard
  ) {
    return (
      <EmptyState
        {...emptyState}
        LottiePlayer={<Lottie src="no-data" />}
        testId={`${testId}-empty-view`}
      />
    )
  }

  return (
    <>
      {header && <PageHeader {...header} />}
      {introduction}
      <Box flexCol gap={16}>
        {!showSearchAndFilter && (
          <Box className="pb-2">
            <SearchAndFilter
              filters={[]}
              {...searchAndFilter}
              testId={`${testId}-search-and-filter`}
            />
          </Box>
        )}
        {tableHead}
        {isLoading && (
          <Box>
            <RowsSkeletonLoader rows={Number(pageSize)} />
          </Box>
        )}
        {!isLoading && !hasResults && (
          <NoSearchResults clearSearchCriteria={resetSearchAndFilters} />
        )}
        {!isLoading && hasResults && children}
        {pagination && <Pagination {...pagination} testId={`${testId}-pagination`} />}
      </Box>
    </>
  )
}
